/**
 * Code Events — banners for discount-code events, e.g. "20% OFF Shorts and
 * Skirts for 48 Hours. Use code SUMMER20" (Theme settings > Code Events).
 *
 * Liquid decides which events apply to a product and renders them
 * (snippets/code-event-match.liquid, snippets/code-event.liquid). This file
 * keeps them honest in the browser, because a cached page outlives the moment
 * it was rendered:
 *   - <code-event data-start data-end> (epoch ms; start 0 = no start date)
 *     hides itself before its start and after its end, and reveals itself at
 *     the start, so an event begins and ends on time on a page nobody reloads.
 *   - [data-code-event-copy] copies the code to the clipboard.
 * The banner's timer is the theme's standard <countdown-timer>
 * (assets/countdown-timer.js), so it takes the same formatting options.
 *
 * The code is display-only: the theme cannot see discounts, so it never
 * checks or applies one. Loaded by layout/theme.liquid only while an event
 * is live or starts within 24 hours.
 */
(() => {
  const theme = (window.theme = window.theme || {});
  const CE = (theme.codeEvents = theme.codeEvents || {});

  // setTimeout fires immediately for delays above 2^31 - 1 ms (~24.8 days).
  CE.MAX_DELAY = 2147483647;

  /** 'pending' before start, 'live' until end, then 'ended'. A missing end is 'ended'. */
  CE.state = (now, start, end) => {
    if (!(end > 0) || now >= end) return 'ended';
    if (now < start) return 'pending';
    return 'live';
  };

  /** Milliseconds until the state next changes, capped for setTimeout; null once ended. */
  CE.nextChange = (now, start, end) => {
    const state = CE.state(now, start, end);
    if (state === 'ended') return null;
    return Math.min((state === 'pending' ? start : end) - now, CE.MAX_DELAY);
  };

  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('code-event')) return;

  class CodeEvent extends HTMLElement {
    connectedCallback() {
      this.start = Number(this.dataset.start) || 0;
      this.end = Number(this.dataset.end) || 0;
      this.update();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    update() {
      clearTimeout(this.timer);
      const now = Date.now();
      this.hidden = CE.state(now, this.start, this.end) !== 'live';

      const delay = CE.nextChange(now, this.start, this.end);
      if (delay !== null) this.timer = setTimeout(() => this.update(), delay + 50);
    }
  }

  customElements.define('code-event', CodeEvent);

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    if (!ok) throw new Error('copy failed');
  };

  document.addEventListener('click', async (evt) => {
    const button = evt.target.closest('[data-code-event-copy]');
    if (!button) return;
    evt.preventDefault();

    const event = button.closest('code-event');
    const status = event && event.querySelector('[data-code-event-status]');
    try {
      await copyText(button.dataset.codeEventCopy);
    } catch (err) {
      // Leave the code visible to copy by hand; nothing else to do.
      return;
    }

    const label = button.dataset.copiedLabel || 'Copied';
    button.classList.add('is-copied');
    button.setAttribute('data-copied', label);
    if (status) status.textContent = `${label}: ${button.dataset.codeEventCopy}`;
    clearTimeout(button.copiedTimer);
    button.copiedTimer = setTimeout(() => {
      button.classList.remove('is-copied');
      if (status) status.textContent = '';
    }, 2000);
  });
})();
